import HaveTileCanvas from 'components/common/recommendation-tiles/have-tile-canvas';
import ReadoutBar from 'components/common/recommendation-tiles/readout-bar';
import RecommendationsList from 'components/common/recommendation-tiles/recommendations-list';
import WantComponentPicker from 'components/common/recommendation-tiles/want-component-picker';
import store from 'app/store';
import { setSystemRecommendationCatalogCodes } from 'features/structure/structure-slice';
import { ReactNode, useEffect, useRef, useState } from 'react';
import axios from 'utils/api';
import { furnaceFuelFromDuctedSelections } from 'utils/furnace-fuel';
import { DuctedFuelToken } from 'utils/kept-system-type-annotation';
import { SystemCountsByCode } from 'utils/recommendation-system-counts';
import { RecommendationsStripVisibleSection } from 'utils/recommendations-strip-visible-section-types';
import { CompositionPicks, FurnaceFuel } from 'utils/system-composition-types';
import {
  BlowerDwelling,
  buildAttributionMap,
  buildWantCanvasOptions,
  computeReadout,
  dedupeRecommendationsById,
  filterRecommendationsByFuel,
  fuelTypeForDerive,
  HaveTileRef,
  parseFingerprintInventory,
  toggleHaveSelection
} from 'utils/recommendation-tiles-logic';
import {
  catalogCodeUnion,
  asSingleWantIds,
  computeDefaultDesiredMulti,
  computeDefaultDesiredSingle,
  desiredSelectPlaceholder,
  extractRecommendedCodes,
  flattenWantRecommendations,
  hasRecommendationAnchor,
  isContextForSession,
  isRecommendationsCheckboxDisabled,
  orderSystemTypeOptions,
  RecommendationContext,
  RecommendationSelections,
  RecommendationsResult,
  reconcileDerivedSelection,
  SavedRecommendationSelections,
  ScenarioRecommendation,
  shouldArmAutoCheckOnSeed,
  SystemTypeOption,
  WantRecommendationGroup,
  wantOnlyFallbackCodes
} from 'utils/recommendations-strip-logic';

type RecommendationsStripProps = {
  systemUniqueId: string;
  componentsKey: string;
  useRecommendations: boolean;
  onToggleUseRecommendations: () => void;
  // Renders the LES-3 "Only show recommended system types" checkbox
  // (default true — the CCB modal browse context keeps it). The Equipment
  // Details step wrapper passes false since sub-build 3: the wants gateway
  // makes every check recommended-bounded, so the checkbox has no fetch
  // meaning there.
  showUseRecommendationsCheckbox?: boolean;
  onRecommendationsChange: (result: {
    matched: boolean;
    recommendedCodes: string[];
    // Want-only fallback bound (AHA-6681): set ONLY by the no-anchor
    // report — the union of the selected want types' own compatibility
    // codes. matched/recommendedCodes semantics stay byte-identical (the
    // CCB modal destructures only those two and never sees this field);
    // the Equipment Details wrapper is the sole consumer.
    wantOnlyCodes?: string[];
  }) => void;
  // Fired synchronously when a recommendations refetch starts, BEFORE the
  // POST is awaited. The strip renders as a child of its host, so this
  // effect runs before the host's fetch effect in the same commit — the CCB
  // modal records pending in a ref its fetch effect reads to abstain from
  // pairing fresh deps with the codes this POST is about to replace
  // (AHA-6681 stale-codes race). Cleared by onRecommendationsChange on
  // every settle path (success, error, no-anchor). Optional: the Equipment
  // Details step host doesn't use it.
  onRecommendationsPending?: () => void;
  // Controlled-mode gate (AHA-6681 LES-2): when present, the context's
  // savedSelections seed the strip on system switch (restored = dirty) and
  // every USER change — plus a dirty-want prune by an options refresh —
  // reports the full current selection here for debounced persistence.
  // Absent (the CCB modal), the strip behaves exactly as before: no new
  // effects, no callback invocations, savedSelections never read.
  onSelectionsChange?: (selections: RecommendationSelections) => void;
  // Fired once per applied system-switch restore in controlled mode with
  // the full saved payload (AHA-6681 LES-4): the step wrapper lifts
  // savedSelections.lastResults into Redux from here — the context GET is
  // the only reader of the persisted JSON. Never fires in uncontrolled
  // mode (the CCB passes neither controlled-mode prop).
  onSavedSelectionsRestore?: (saved: SavedRecommendationSelections) => void;
  // Presentation variant (AHA-6681 tile panel). 'tiles' renders the have
  // component-tile canvas + readout bar, the want COMPONENT PICKER (compose
  // a system type from per-slot component picks instead of browsing the
  // 80+ composite-type tiles), and the flat deduped recommendations list —
  // and adds the virtual-derive flow feeding the same DECISION-2 union
  // machinery. Default 'dropdown' keeps the pre-tile render byte-identical
  // (the CCB modal contract and every existing suite).
  variant?: 'dropdown' | 'tiles';
  // SYSTEM SIZE row + sizing-path affordance (AHA-6681 tonnage). Rendered
  // inside the want section of the TILES variant only; the host owns all
  // tonnage state/derivation/persistence — the strip is just the placement.
  // Absent (dropdown mode / the CCB modal): byte-identical render.
  tonnageSlot?: ReactNode;
  // Structure dwelling mode filtering the tiles-variant Blower offers
  // (Geoff ruling, AHA-6681 dwelling filter): 'mobile' offers only
  // mobile-home furnaces + Modular Blower, 'standard' only standard
  // furnaces + MB, 'modular' everything. Absent = 'standard' (Geoff's
  // unknown-subtype rule). The host resolves it from the structure
  // subtype — the strip has no redux and just forwards it to the have
  // canvas and the want component picker; the dropdown variant never
  // renders either, so the CCB modal is untouched.
  dwelling?: BlowerDwelling;
  // DISPLAY-ONLY fuel narrowing of the tiles variant (AHA-6796): filters
  // the flat recommendations list by fuel token. The host now drives this
  // from the want picker's single fuel question (and the equipment seed
  // until that pick lands). The dwelling-prop precedent: render-pure —
  // never an effect dep, never emits selections, never changes any POST.
  // Absent/empty (and the dropdown variant / CCB modal): byte-identical.
  wantFuelNarrow?: DuctedFuelToken[];
  // Want-picker fuel changes. The host writes the shared
  // ductedFilters.fuelSelections so Choose Systems and the compatible
  // counts follow the one fuel question on this card.
  onWantFuelChange?: (fuel: FurnaceFuel | null) => void;
  onCompositionPicksChange?: (
    picks: CompositionPicks,
    wantIds: number[]
  ) => void;
  visibleSection?: RecommendationsStripVisibleSection;
  systemCountsByCode?: SystemCountsByCode | null;
};

const FETCH_ERROR_MESSAGE = 'Recommendations are unavailable right now.';

const SELECT_LABEL_CLASS =
  'mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500';
const SELECT_CLASS =
  'w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs';

// Strip between the equipment panel and the filter bar (port of
// alpine-client's EquipmentCheck/RecommendationsStrip.js). Owns all
// recommendation state locally (no Redux); the parent modal only receives
// { matched, recommendedCodes } through onRecommendationsChange and controls
// the checkbox via useRecommendations/onToggleUseRecommendations. Errors here
// are non-fatal: the combinations table is never blocked by a failed strip
// fetch.
export default function RecommendationsStrip({
  systemUniqueId,
  componentsKey,
  useRecommendations,
  onToggleUseRecommendations,
  showUseRecommendationsCheckbox = true,
  onRecommendationsChange,
  onRecommendationsPending,
  onSelectionsChange,
  onSavedSelectionsRestore,
  variant = 'dropdown',
  tonnageSlot,
  dwelling,
  wantFuelNarrow,
  onWantFuelChange,
  onCompositionPicksChange,
  visibleSection = 'both',
  systemCountsByCode
}: RecommendationsStripProps) {
  const [context, setContext] = useState<RecommendationContext | null>(null);
  // The systemUniqueId the applied context belongs to (AHA-6681 stale
  // cross-session race). State — not a ref — so it commits atomically with
  // setContext in the same batch: the recs effect can never observe a fresh
  // context paired with a stale owner id or vice versa. The context effect
  // bails on a null id without clearing context, so this pair is how the
  // recs effect detects a context outrun by a swap (or orphaned by a
  // discard, structurally impossible for this host).
  const [contextSystemId, setContextSystemId] = useState<string | null>(null);
  const [contextError, setContextError] = useState<string | null>(null);

  const [haveId, setHaveId] = useState<number | null>(null);
  const [refrigerant, setRefrigerant] = useState('');
  const [age, setAge] = useState('');
  // Multi-select wants in selection order (append on check, filter on
  // uncheck) — the POST sends them as desiredSystemTypeIds (AHA-6681).
  const [wantIds, setWantIds] = useState<number[]>([]);
  // Display-only narrowing of the want checkbox list; never touches wantIds,
  // so checked-but-hidden wants stay selected (and posted).
  const [wantFilter, setWantFilter] = useState('');
  // Refs, not state: dirtiness only changes inside the same events that set
  // the selects, and it must never re-run the fetch effects by itself.
  const wantDirtyRef = useRef(false);
  const haveDirtyRef = useRef(false);
  const refrigerantDirtyRef = useRef(false);
  // Armed by a user CHECK of a want — or by an app seed introducing the
  // first want (AHA-6681 requirement A); consumed by the recs response
  // handler, which auto-enables "Only show recommended system types" iff the
  // response yields searchable codes. A ref, not state: it must never re-run
  // the fetch effect, and it survives a reconcile-superseded response so the
  // follow-up (applied) response consumes it.
  const autoCheckIntentRef = useRef(false);
  // Suppresses the seed-arm after a system-switch restore carrying an
  // explicit useRecommendations:false — the user's saved unchecked
  // preference outlives a reseed (restore-vs-seed conflict: saved preference
  // wins). Recomputed on every applied system switch so stale suppression
  // never leaks across systems; cleared by a user want CHECK (a fresh
  // gesture supersedes the saved preference). Applies only on a
  // fingerprint-MATCHED restore (AHA-6681 DECISION 1): a mismatched record
  // describes the old inventory, so the overwrite path resets it to
  // fresh-derive semantics (no suppression, no intent).
  const seedAutoCheckSuppressedRef = useRef(false);
  // Fingerprint of the last APPLIED context (AHA-6681 DECISION 1): a
  // componentsKey refetch whose fingerprint differs means the inventory
  // changed while mounted — derived values then OVERWRITE saved/dirty
  // selections instead of the dirty-wins reconcile. Null until the first
  // applied success (and against an old API that sends no fingerprint,
  // which keeps today's reconcile behavior).
  const lastAppliedFingerprintRef = useRef<string | null>(null);
  // Set by the derive-and-overwrite path, consumed by the recs effect after
  // the wants reseed: the re-derived selection (new fingerprint included)
  // is then persisted via onSelectionsChange — the one deliberate exception
  // to "restores/derivations never emit" (DECISION 1: the persisted record
  // must move to the new inventory's derivation).
  const pendingOverwritePersistRef = useRef(false);
  // The response handler runs long after its effect closure was captured and
  // useRecommendations is deliberately not an effect dep (one POST per state
  // change), so this ref is its only current view of the checkbox.
  const useRecommendationsRef = useRef(useRecommendations);
  useRecommendationsRef.current = useRecommendations;
  // Same ref pattern: the callback (and its presence, which gates controlled
  // mode) is read inside long-lived effect closures without ever entering
  // effect deps — a parent re-render can't re-trigger a fetch.
  const onSelectionsChangeRef = useRef(onSelectionsChange);
  onSelectionsChangeRef.current = onSelectionsChange;
  const onSavedSelectionsRestoreRef = useRef(onSavedSelectionsRestore);
  onSavedSelectionsRestoreRef.current = onSavedSelectionsRestore;
  // Distinguishes a real system switch (full reset) from a componentsKey-only
  // context refetch (reconcile, AHA-6681). Updated only on applied success so
  // an errored system-switch fetch still full-resets on the next success.
  const prevSystemUniqueIdRef = useRef<string | null>(null);

  const [desiredOptions, setDesiredOptions] = useState<SystemTypeOption[]>([]);
  const [recommendationsByWant, setRecommendationsByWant] = useState<
    WantRecommendationGroup[]
  >([]);
  const [matched, setMatched] = useState(false);
  const [mergedAcrossAgeBuckets, setMergedAcrossAgeBuckets] = useState(false);
  const [recsError, setRecsError] = useState<string | null>(null);

  // ---- Tile-mode state (AHA-6681 tile panel) — inert in dropdown mode. ----
  // Virtual have-tile selection + fuel-variant literal, persisted as
  // virtualCodes/virtualFuel on every tile-mode selections emit (the PATCH
  // is a full record replace, so every emit must carry them).
  const [virtualCodes, setVirtualCodes] = useState<string[]>([]);
  const [virtualFuel, setVirtualFuel] = useState<string | null>(null);
  // Candidate union from the last virtual derive; null = no derive yet
  // (empty canvas). On a virtual canvas this REPLACES the context's derived
  // candidates as the DECISION-2 union input — feeding, not forking, the
  // existing machinery.
  const [virtualCandidateIds, setVirtualCandidateIds] = useState<
    number[] | null
  >(null);
  // Legacy read-compat (§F): a fingerprint-matched record with a haveId but
  // NO virtualCodes renders no tiles and a "Saved pick" readout; the haveId
  // anchors the recs POST exactly as before. First tile interaction clears
  // it — tiles take over.
  const [legacySavedPickId, setLegacySavedPickId] = useState<number | null>(
    null
  );
  // The server's flat deduped ranked view (§J); falls back to a client-side
  // dedupe of the per-want groups against an old API.
  const [flatRecommendations, setFlatRecommendations] = useState<
    ScenarioRecommendation[]
  >([]);
  // Ref mirrors for effect-closure reads (same pattern as the callback
  // refs): the recs effect's dirty-prune emit must carry the CURRENT tile
  // state without virtualCodes/virtualFuel joining its deps.
  const virtualCodesRef = useRef(virtualCodes);
  virtualCodesRef.current = virtualCodes;
  const virtualFuelRef = useRef(virtualFuel);
  virtualFuelRef.current = virtualFuel;
  // Latest-wins guard for the derive POST: a stale response must never
  // clobber the state of a newer tile gesture.
  const deriveNonceRef = useRef(0);

  // Derive-current-system flow (§A/§D): resolves the virtual canvas into
  // the have anchor (single candidate) or the blank-have candidate union
  // (multiple/zero) — exactly the state shapes the existing DECISION-2
  // machinery consumes. An empty canvas skips the POST. emitAfter persists
  // the derive-driven change through the existing selections PATCH;
  // restores pass false (restores never emit).
  const fireDerive = async (
    codes: string[],
    fuel: string | null,
    emitAfter: boolean
  ) => {
    deriveNonceRef.current += 1;
    const nonce = deriveNonceRef.current;
    let derivedHaveId: number | null = null;
    let candidateIds: number[] = [];
    if (codes.length > 0) {
      try {
        const response = await axios.post<{
          currentSystemTypeId: number | null;
          candidateSystemTypeIds: number[];
        }>('/existing-equipment/derive-current-system', {
          componentCodes: codes,
          fuelType: fuelTypeForDerive(fuel)
        });
        derivedHaveId = response.data?.currentSystemTypeId ?? null;
        candidateIds = response.data?.candidateSystemTypeIds ?? [];
      } catch {
        // Non-fatal, like every strip fetch: a failed derive behaves as a
        // zero-candidate result and the next tile change retries.
      }
    }
    if (nonce !== deriveNonceRef.current) return;
    setHaveId(derivedHaveId);
    setVirtualCandidateIds(candidateIds);
    if (emitAfter) {
      onSelectionsChangeRef.current?.({
        wantIds,
        haveId: derivedHaveId,
        refrigerant: refrigerant || null,
        age: age || null,
        fingerprint: context?.derived?.fingerprint,
        virtualCodes: codes,
        virtualFuel: fuel
      });
    }
  };

  useEffect(() => {
    if (!systemUniqueId) return undefined;
    let cancelled = false;
    const fetchContext = async () => {
      setContextError(null);
      try {
        const response = await axios.get<RecommendationContext>(
          `/existing-equipment/recommendation-context/${systemUniqueId}`
        );
        if (cancelled) return;
        const { data } = response;
        // Keep the per-system full-universe catalog mirror live (AHA-6796
        // fuel vocabulary): every context refetch — system switch and
        // componentsKey churn, which encodes equipment edits AND
        // dwelling/subtype flips — re-lands the codes the fuel chip rows
        // derive their vocabulary from. Direct store dispatch on purpose:
        // the strip is deliberately redux-free (the CCB modal mounts it
        // without any store coupling), and this is a write-only mirror
        // update keyed to the REQUESTED system.
        store.dispatch(
          setSystemRecommendationCatalogCodes({
            systemUniqueId,
            codes: catalogCodeUnion(data?.systemTypes)
          })
        );
        setContext(data);
        // Same batch as setContext: the pair is only ever observed coherent.
        setContextSystemId(systemUniqueId);
        if (prevSystemUniqueIdRef.current !== systemUniqueId) {
          // Real system switch: full reset, exactly the pre-AHA-6681
          // behavior — except in controlled mode (onSelectionsChange
          // present) with saved selections in the context, which restore
          // instead of derive (LES-2). Restored fields count as DIRTY so
          // reconciliation/pruning preserves them like live user choices;
          // absent keys fall back to the derived value, not dirty.
          // DECISION-1 precondition: the restore only applies when the
          // saved record's fingerprint matches the current derivation's — a
          // record made under a different inventory (mismatch, or a legacy
          // row without a fingerprint) is overwritten by the fresh
          // derivation and re-persisted (pendingOverwritePersistRef). Age
          // is exempt: user-only input with no derived counterpart, so the
          // overwrite leaves the saved value in place (DECISION 4).
          const saved = onSelectionsChangeRef.current
            ? data?.savedSelections ?? null
            : null;
          const fingerprintMatches =
            !saved || saved.fingerprint === data?.derived?.fingerprint;
          const restorable = fingerprintMatches ? saved : null;
          if (restorable && 'haveId' in restorable) {
            // Explicit null = user cleared — stays cleared.
            setHaveId(restorable.haveId ?? null);
            haveDirtyRef.current = true;
          } else {
            setHaveId(data?.derived?.currentSystemTypeId ?? null);
            haveDirtyRef.current = false;
          }
          if (restorable && 'refrigerant' in restorable) {
            setRefrigerant(restorable.refrigerant ?? '');
            refrigerantDirtyRef.current = true;
          } else {
            setRefrigerant(data?.derived?.refrigerant ?? '');
            refrigerantDirtyRef.current = false;
          }
          // Age is user-only input (never reconciled) — no dirty ref
          // exists, and a fingerprint mismatch still restores it.
          setAge(saved && 'age' in saved ? saved.age ?? '' : '');
          if (restorable) {
            // Dirty restore: the recs effect prunes these against the fresh
            // options (computeDefaultDesiredMulti, wantDirty=true) exactly
            // like in-session choices instead of like-for-like re-seeding.
            // An EMPTY restored set is dirty too (AHA-6681 QA F4): the
            // selections PATCH always persists wantIds and only its records
            // carry a fingerprint, so a fingerprint-MATCHED empty set can
            // only mean the user explicitly cleared it (DECISION 1) — the
            // strip must not visually reseed over a persisted []. Never-
            // saved, legacy, and mismatched records arrive here as
            // restorable=null and keep seeding.
            // Tiles is single-want (the picker only shows wantIds[0]): a
            // legacy multi-id restore would otherwise POST extras the
            // picker cannot represent. Dropdown keeps the full set.
            setWantIds(
              variant === 'tiles'
                ? asSingleWantIds(restorable.wantIds)
                : restorable.wantIds
            );
            wantDirtyRef.current = true;
          } else {
            setWantIds([]);
            wantDirtyRef.current = false;
          }
          setWantFilter('');
          // Want-restore alone never arms the auto-check; a persisted
          // checked preference (useRecommendations: true, LES-3) is the
          // deliberate exception. It still enables only through the recs
          // response's gated consume (searchable codes + checkbox currently
          // off), so an unsearchable restore stays off. Inert in
          // uncontrolled mode (CCB): `saved` is null there. On a
          // fingerprint mismatch both refs reset to fresh-derive semantics
          // (DECISION 1): the saved flags described the old inventory.
          autoCheckIntentRef.current = restorable?.useRecommendations === true;
          // Only an explicitly-saved FALSE suppresses the seed-arm; an
          // absent flag (never persisted) or true never does.
          seedAutoCheckSuppressedRef.current =
            !!restorable && restorable.useRecommendations === false;
          prevSystemUniqueIdRef.current = systemUniqueId;
          // A mismatched saved record is stale: re-persist the fresh
          // derivation once the recs effect has reseeded the wants.
          if (saved && !fingerprintMatches) {
            pendingOverwritePersistRef.current = true;
          }
          // Applied system-switch restore: hand the full saved payload to
          // the controlled parent (LES-4 lastResults hop). `saved` is null
          // in uncontrolled mode, so the CCB never fires this; a
          // componentsKey-only refetch takes the other branch entirely.
          // Skipped on a fingerprint mismatch: the persisted lastResults
          // summary describes the OLD inventory (the live debounced check
          // re-populates it).
          if (restorable) {
            onSavedSelectionsRestoreRef.current?.(restorable);
          }
          // Tile-mode system switch (AHA-6681 tile panel): reset the tile
          // state for the new system, then seed it from a fingerprint-
          // MATCHED record. A mismatched/legacy record arrives here as
          // restorable=null and stays blank — the overwrite path persists
          // virtualCodes: [] after the reseed.
          if (variant === 'tiles') {
            setVirtualCodes([]);
            setVirtualFuel(null);
            setVirtualCandidateIds(null);
            setLegacySavedPickId(null);
            const inventory = parseFingerprintInventory(
              data?.derived?.fingerprint
            );
            const virtualCanvas =
              inventory != null && inventory.codes.length === 0;
            if (virtualCanvas && restorable) {
              const savedCodes = restorable.virtualCodes ?? [];
              if (savedCodes.length > 0) {
                // Restore seeds the tiles (fuel names the variant tile —
                // or the fuel-unknown fallback for a legacy fuel-null
                // furnace save, which stays union-driven); a null restored
                // haveId means the record rode the union — re-fire the
                // derive to rebuild the candidate list (restores never
                // emit, so no persist).
                setVirtualCodes(savedCodes);
                setVirtualFuel(restorable.virtualFuel ?? null);
                const restoredHaveId = restorable.haveId ?? null;
                if (restoredHaveId == null) {
                  fireDerive(savedCodes, restorable.virtualFuel ?? null, false);
                } else {
                  setVirtualCandidateIds([restoredHaveId]);
                }
              } else if (
                !('virtualCodes' in restorable) &&
                (restorable.haveId ?? null) != null
              ) {
                // Legacy record (§F): haveId but no virtualCodes key —
                // readout shows the saved pick, no tiles; the restored
                // haveId anchors the recs POST exactly as today. An
                // explicit empty virtualCodes: [] stays blank instead
                // (QA-F4: no visual reseed over an explicit clear).
                setLegacySavedPickId(restorable.haveId as number);
              }
            }
          }
        } else if (
          data?.derived?.fingerprint != null &&
          data.derived.fingerprint !== lastAppliedFingerprintRef.current
        ) {
          // componentsKey refetch with a CHANGED fingerprint: the inventory
          // changed while mounted, so the fresh derivation OVERWRITES
          // saved/dirty have/refrigerant (DECISION 1 — a manual pick holds
          // only while the inventory is unchanged) and clears the wants so
          // the recs effect reseeds them. Age is never overwritten
          // (DECISION 4). The re-derived record is persisted after the
          // reseed via pendingOverwritePersistRef.
          setHaveId(data?.derived?.currentSystemTypeId ?? null);
          haveDirtyRef.current = false;
          setRefrigerant(data?.derived?.refrigerant ?? '');
          refrigerantDirtyRef.current = false;
          setWantIds((prev) => (prev.length === 0 ? prev : []));
          wantDirtyRef.current = false;
          // Fresh-derive semantics: any restored suppression/intent
          // described the old inventory's record; the seed-arm then behaves
          // exactly as on a fresh system.
          autoCheckIntentRef.current = false;
          seedAutoCheckSuppressedRef.current = false;
          // Tile mode: an inventory change while mounted drops the local
          // tile state both ways (§D) — virtual→real (first real component
          // supersedes the virtual canvas with the locked readout) and real→zero
          // (fingerprint back to 'v1||||' → blank canvas). The overwrite
          // persist below writes virtualCodes: [] so no stale codes survive
          // in the record.
          if (variant === 'tiles') {
            setVirtualCodes([]);
            setVirtualFuel(null);
            setVirtualCandidateIds(null);
            setLegacySavedPickId(null);
          }
          if (onSelectionsChangeRef.current) {
            pendingOverwritePersistRef.current = true;
          }
        } else {
          // componentsKey-only refetch with an UNCHANGED fingerprint (e.g.
          // an unslotted component edit, or an old API without
          // fingerprints): preserve dirty user choices that the fresh
          // context still offers; re-derive the rest. Functional updates
          // protect against user edits landing while the fetch was in
          // flight. Age is user-only input — never touched. wantIds are
          // reconciled by the recs effect via computeDefaultDesiredMulti.
          setHaveId((prev) =>
            reconcileDerivedSelection(
              haveDirtyRef.current,
              prev,
              data?.derived?.currentSystemTypeId ?? null,
              (data?.systemTypes ?? []).map((t) => t.id)
            )
          );
          setRefrigerant((prev) =>
            reconcileDerivedSelection(
              refrigerantDirtyRef.current,
              prev,
              data?.derived?.refrigerant ?? '',
              ['', ...(data?.refrigerantOptions ?? [])]
            )
          );
        }
        lastAppliedFingerprintRef.current = data?.derived?.fingerprint ?? null;
      } catch {
        if (cancelled) return;
        setContextError(FETCH_ERROR_MESSAGE);
        // The error panel hides the checkbox, so the parent must not keep
        // filtering by codes the user can no longer see or uncheck: an empty
        // report clears the narrow and force-unchecks (the hosts'
        // isRecommendationsCheckboxDisabled contract). Dropping the stale
        // context stops the recs effect from re-reporting against a context
        // the user can't see; the next successful fetch rebuilds everything.
        // autoCheckIntentRef stays deliberately armed: it encodes an
        // unconsumed gesture/persisted preference, is inert while context is
        // null, and its response-gated consume on recovery re-checks the box
        // this force-uncheck cleared.
        setContext(null);
        // Keep the (context, owner id) pair coherent: a null context owns no
        // session.
        setContextSystemId(null);
        onRecommendationsChange({ matched: false, recommendedCodes: [] });
      }
    };
    fetchContext();
    return () => {
      cancelled = true;
    };
    // onRecommendationsChange is a parent-owned handler excluded so a parent
    // re-render can't re-trigger the fetch (same contract as the recs effect
    // below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemUniqueId, componentsKey]);

  useEffect(() => {
    if (!context) return undefined;
    // Cross-session guard (AHA-6681): the context effect bails on a null
    // systemUniqueId without clearing context, so a swap (old id → new id)
    // re-runs this effect with the OLD context paired with the NEW id
    // before the context refetch settles — the CCB modal's swap-on-reopen
    // (the debug menu snapshots the system id at click time and never
    // clears it, so a reseed while closed swaps the mounted strip's id on
    // reopen). Unguarded, the POST would settle AFTER the modal's
    // render-adjust already reset its codes/checkbox for the session
    // change, repopulating recommendedCodes with the dead session's codes —
    // and the armed intent below could re-check the box against them. Bail
    // mirrors the no-anchor path: clear the strip's display state so the
    // old ranked list never renders against the new session, disarm the
    // intent (a gesture armed pre-swap belongs to the dead session), and
    // report empty — a synchronous settle for any window a cancelled
    // in-flight POST left dangling (its cleanup's `cancelled` flag means it
    // never reports). Idempotent against the modal's own reset.
    // pendingOverwritePersistRef is deliberately NOT consumed here: the
    // fingerprint-guarded consumes belong to runs observing a coherent
    // context (the closure's selections belong to the dead session), and
    // the post-swap run consumes it with fresh values. Critically,
    // onRecommendationsPending() stays below the early returns so this
    // path never opens a window nothing would clear.
    if (!isContextForSession(contextSystemId, systemUniqueId)) {
      setDesiredOptions([]);
      setRecommendationsByWant([]);
      setFlatRecommendations([]);
      setMatched(false);
      setMergedAcrossAgeBuckets(false);
      setRecsError(null);
      autoCheckIntentRef.current = false;
      onRecommendationsChange({ matched: false, recommendedCodes: [] });
      return undefined;
    }
    // Effective DECISION-2 candidates: a tile-mode VIRTUAL canvas (fingerprint
    // present with zero slotted codes) anchors on the virtual derive's
    // candidate union instead of the context's derived candidates — the one
    // change that feeds the tile flow into the existing union machinery.
    // Equipment-backed canvases and old-API (no fingerprint) responses keep
    // the context candidates exactly as before.
    const tileInventory =
      variant === 'tiles'
        ? parseFingerprintInventory(context.derived?.fingerprint)
        : null;
    const tileVirtualCanvas =
      tileInventory != null && tileInventory.codes.length === 0;
    const candidateIds = tileVirtualCanvas
      ? virtualCandidateIds ?? []
      : context.derived?.candidateSystemTypeIds ?? [];
    // Blank-as-union (DECISION 2): a blank have with derivation candidates
    // fetches the union across them. A blank WITHOUT candidates has no
    // current system to anchor on (no existing equipment, or none the
    // derivation could identify): with wants selected it resolves them in
    // the API's no-current-system mode below; with none it is the
    // clear-everything dead-end.
    const unanchored = !hasRecommendationAnchor(haveId, candidateIds);
    if (unanchored && wantIds.length === 0) {
      setDesiredOptions([]);
      setRecommendationsByWant([]);
      setFlatRecommendations([]);
      setMatched(false);
      setMergedAcrossAgeBuckets(false);
      setRecsError(null);
      autoCheckIntentRef.current = false;
      // An overwrite that re-derived to an unanchored blank still persists
      // the re-derived record (DECISION 1): nothing reseeds here, so
      // consume the pending persist with the cleared selection. The
      // fingerprint guard mirrors the response-side consume below. Tile
      // mode pins virtualCodes: [] into the payload — the overwrite must
      // never leave stale virtual codes in the record (§D).
      if (
        pendingOverwritePersistRef.current &&
        (context.derived?.fingerprint ?? null) ===
          lastAppliedFingerprintRef.current
      ) {
        pendingOverwritePersistRef.current = false;
        onSelectionsChangeRef.current?.({
          wantIds,
          haveId: null,
          refrigerant: refrigerant || null,
          age: age || null,
          fingerprint: context.derived?.fingerprint,
          ...(variant === 'tiles'
            ? { virtualCodes: [], virtualFuel: null }
            : {})
        });
      }
      // The no-anchor report shape (AHA-6681 want-only fallback):
      // matched/recommendedCodes stay the byte-identical empty report and
      // wantOnlyCodes rides along — [] here, since no wants are selected.
      onRecommendationsChange({
        matched: false,
        recommendedCodes: [],
        wantOnlyCodes: wantOnlyFallbackCodes(wantIds, context.systemTypes)
      });
      return undefined;
    }
    // Unanchored with wants: the want types' own code union is the
    // degraded bound should the no-current-system response carry no codes
    // (or fail) — the Equipment Details wrapper prefers recommendedCodes
    // and falls back to these (resolveEffectiveRecommendedCodes).
    const unanchoredReportFields = unanchored
      ? { wantOnlyCodes: wantOnlyFallbackCodes(wantIds, context.systemTypes) }
      : {};
    // Mark the refetch in flight only AFTER the early returns above: the
    // no-anchor path settles synchronously (its onRecommendationsChange
    // report clears any window) and must never open one, and the
    // context-error path stays settled — its catch reports synchronously
    // before the context=null re-render, whose !context early return never
    // re-marks. Both the success and error paths below clear via their
    // existing onRecommendationsChange calls.
    onRecommendationsPending?.();
    let cancelled = false;
    const fetchRecs = async () => {
      setRecsError(null);
      try {
        const response = await axios.post<RecommendationsResult>(
          '/existing-equipment/recommendations',
          unanchored
            ? // No current system: no triple to send — the server resolves
              // each want's current-agnostic list (its like-for-like
              // scenarios), which is how a plain "Heat Pump" want reaches
              // the Next Gen types the picker never offers.
              { noCurrentSystem: true, desiredSystemTypeIds: wantIds }
            : {
                // Union mode posts the candidate list INSTEAD of the single
                // id (the server treats a non-empty list as the anchor); an
                // explicit pick keeps today's single-type body exactly.
                ...(haveId == null
                  ? { currentSystemTypeIds: candidateIds }
                  : { currentSystemTypeId: haveId }),
                currentSystemRefrigerant: refrigerant || null,
                currentSystemAge: age || null,
                desiredSystemTypeIds: wantIds
              }
        );
        if (cancelled) return;
        const { data } = response;
        const options = data?.desiredOptions ?? [];
        setDesiredOptions(options);
        // Non-dirty seeding: the API's want-seed union first (full-inventory
        // type ∪ replacing-subset types, AHA-6681 batch 4), like-for-like
        // [haveId] fallback — or dropping no-longer-available dirty choices
        // (the still-available subset survives). Tiles collapses that
        // union to the first id (computeDefaultDesiredSingle) so the
        // picker, the recs POST, and handleWantCompose share one type.
        // When this changes wantIds the effect re-runs and the follow-up
        // response carries the recommendations for the reconciled wants.
        // Reference equality on an unchanged selection is what stops the
        // loop.
        // Re-seed on a keep/replace or inventory change comes free from the
        // fingerprint overwrite path, which clears wantIds and wantDirty
        // (keep/replace toggles change the fingerprint via
        // replacingSlottedCodes).
        const nextWantIds = (
          variant === 'tiles'
            ? computeDefaultDesiredSingle
            : computeDefaultDesiredMulti
        )(
          haveId,
          options,
          wantDirtyRef.current,
          wantIds,
          context.derived?.replacingWantSystemTypeIds ?? [],
          // Tiles prune universe = the canvas union (amendment B): the want
          // canvas offers the full catalog, so a dirty manual pick outside
          // the have-keyed desiredOptions must SURVIVE a later derivation
          // (the clobber the amendment warns about) — only garbage ids
          // drop. Dropdown keeps the desiredOptions-bounded prune.
          variant === 'tiles'
            ? new Set([
                ...options.map((option) => option.id),
                ...context.systemTypes.map((type) => type.id)
              ])
            : undefined,
          // Candidates fallback (task dd2cf6): deliberately the CONTEXT
          // candidates, not the effect's candidateIds — a tiles VIRTUAL
          // canvas substitutes virtualCandidateIds there, and hand-picked
          // tiles must stay seeding-neutral (context candidates are [] for
          // it). Kept equipment-backed inventories are the ruling's scope.
          context.derived?.candidateSystemTypeIds ?? []
        );
        // Candidate for the seed-arm below (requirement A). The arm itself
        // waits until AFTER the consume block: this response's codes reflect
        // the pre-seed (empty) wants, so arming here would be consumed by
        // the same response and read as "nothing searchable".
        const seedIntroducedWants = shouldArmAutoCheckOnSeed(
          wantDirtyRef.current,
          wantIds,
          nextWantIds
        );
        if (nextWantIds !== wantIds) {
          setWantIds(nextWantIds);
          // A prune of DIRTY (user-chosen or restored) wants persists back
          // (LES-2): in-session, a want pruned by a kept-set change does not
          // come back when the kept set reverts, so saving the pruned set
          // keeps the DB matching the screen — otherwise a reload would
          // resurrect wants the UI already dropped. Non-dirty changes here
          // are like-for-like seeding (a derivation, not a choice): never
          // reported.
          if (wantDirtyRef.current) {
            onSelectionsChangeRef.current?.({
              wantIds: nextWantIds,
              haveId,
              refrigerant: refrigerant || null,
              age: age || null,
              fingerprint: context.derived?.fingerprint,
              // The PATCH replaces the whole record, so a tile-mode prune
              // must re-carry the current tile state (ref reads — the tile
              // state deliberately isn't an effect dep).
              ...(variant === 'tiles'
                ? {
                    virtualCodes: virtualCodesRef.current,
                    virtualFuel: virtualFuelRef.current
                  }
                : {})
            });
          }
        }
        // DECISION-1 overwrite persist: the derive-and-overwrite path armed
        // this so the re-derived selection (with the NEW fingerprint) lands
        // in the DB after the wants reseeded — nextWantIds already carries
        // the seed even before the setWantIds re-run applies it. The dirty
        // prune emit above can't double-fire: the overwrite reset wantDirty.
        // Fingerprint guard: a response whose closure predates the
        // overwrite's context commit (its fingerprint isn't the last
        // applied one) must not consume the flag with stale values — the
        // post-overwrite run, which always sees the applied context, does.
        if (
          pendingOverwritePersistRef.current &&
          (context.derived?.fingerprint ?? null) ===
            lastAppliedFingerprintRef.current
        ) {
          pendingOverwritePersistRef.current = false;
          onSelectionsChangeRef.current?.({
            wantIds: nextWantIds,
            haveId,
            refrigerant: refrigerant || null,
            age: age || null,
            fingerprint: context.derived?.fingerprint,
            // Explicit spec addition (§D): the overwrite persist clears
            // virtualCodes so stale virtual tiles never survive an
            // inventory change in the record.
            ...(variant === 'tiles'
              ? { virtualCodes: [], virtualFuel: null }
              : {})
          });
        }
        const groups = data?.recommendationsByWant ?? [];
        setRecommendationsByWant(groups);
        // Flat deduped view (§J): the server field when present, else a
        // client-side first-occurrence dedupe of the groups (old API).
        setFlatRecommendations(
          data?.recommendations ??
            dedupeRecommendationsById(flattenWantRecommendations(groups))
        );
        setMatched(!!data?.matched);
        setMergedAcrossAgeBuckets(!!data?.mergedAcrossAgeBuckets);
        // Codes come from the same groups the strip renders (never the
        // top-level flat list) so display and codes can't drift.
        const codes = extractRecommendedCodes(
          flattenWantRecommendations(groups)
        );
        onRecommendationsChange({
          matched: !!data?.matched,
          recommendedCodes: codes,
          ...unanchoredReportFields
        });
        // A user check armed the intent: auto-enable "Only show recommended"
        // now that this response proves the checkbox would be usable. The
        // modal's handleRecommendationsChange already ran with these codes,
        // so its force-uncheck branch can't fight a searchable response.
        if (autoCheckIntentRef.current) {
          autoCheckIntentRef.current = false;
          if (
            !isRecommendationsCheckboxDisabled(!!data?.matched, codes) &&
            !useRecommendationsRef.current
          ) {
            onToggleUseRecommendations();
          }
        }
        // Seed-arm (requirement A): an app-seeded want counts as a
        // selection. The seeded wantIds re-run this effect and the follow-up
        // response consumes the intent with the real codes. A restored
        // explicit useRecommendations:false suppresses it (saved preference
        // wins) until a user want CHECK clears the suppression.
        if (seedIntroducedWants && !seedAutoCheckSuppressedRef.current) {
          autoCheckIntentRef.current = true;
        }
      } catch {
        if (cancelled) return;
        setRecsError(FETCH_ERROR_MESSAGE);
        autoCheckIntentRef.current = false;
        onRecommendationsChange({
          matched: false,
          recommendedCodes: [],
          ...unanchoredReportFields
        });
      }
    };
    fetchRecs();
    return () => {
      cancelled = true;
    };
    // onRecommendationsChange, onRecommendationsPending, and
    // onToggleUseRecommendations are parent-owned handlers excluded so a
    // parent re-render can't re-trigger the fetch; the
    // data deps below are complete (virtualCandidateIds joins them for the
    // tile-mode virtual union — inert in dropdown mode, where it never
    // changes from null).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    context,
    contextSystemId,
    haveId,
    refrigerant,
    age,
    wantIds,
    systemUniqueId,
    virtualCandidateIds
  ]);

  if (!systemUniqueId) return null;

  if (contextError) {
    return (
      <div className='mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3'>
        <div className='mb-1 text-sm font-semibold text-gray-700'>
          Recommended system types
        </div>
        <div className='text-xs text-gray-400'>{contextError}</div>
      </div>
    );
  }

  if (!context) return null;

  const systemTypeOptions = orderSystemTypeOptions(
    context.systemTypes,
    context.derived?.candidateSystemTypeIds
  );
  // Tile-mode view of the derivation fingerprint (AHA-6681 tile panel):
  // slotted codes → equipment-backed locked readout (no canvas — the
  // ReadoutBar names the system); empty codes → virtual canvas (the derive
  // flow's candidates replace the context's); null (no fingerprint, old
  // API) → readout-only degrade (§F).
  const tileInventory =
    variant === 'tiles'
      ? parseFingerprintInventory(context.derived?.fingerprint)
      : null;
  const tileVirtualCanvas =
    tileInventory != null && tileInventory.codes.length === 0;
  const effectiveCandidateIds = tileVirtualCanvas
    ? virtualCandidateIds ?? []
    : context.derived?.candidateSystemTypeIds;
  // Union mode: with an anchor (explicit have, or blank-with-candidates —
  // DECISION 2) the recs POST fires and desiredOptions seed/attribute; in
  // tiles mode the want canvas is usable either way (amendment B) and the
  // anchor only gates recommendations/dots.
  const anchored = hasRecommendationAnchor(haveId, effectiveCandidateIds);
  // Tiles want universe (amendment B): scenario-backed desiredOptions
  // first, then the remaining full catalog — the component picker builds
  // its composition index from this union, so browse mode works with no
  // have and a seeded/manual want outside the scenario set still resolves
  // its composition. No display ordering/narrowing passes: the picker's
  // per-slot vocabulary is fixed and reachability comes from the index
  // (dwelling filters the blower offer inside the picker; wantFuelNarrow
  // now only narrows the recommendations list). Dropdown resolves from
  // desiredOptions alone, exactly as before.
  const wantCanvasOptions =
    variant === 'tiles'
      ? buildWantCanvasOptions(desiredOptions, context.systemTypes)
      : desiredOptions;
  const flatWantRecommendations = flattenWantRecommendations(
    recommendationsByWant
  );
  const recommendedCodes = extractRecommendedCodes(flatWantRecommendations);
  const checkboxDisabled = isRecommendationsCheckboxDisabled(
    matched,
    recommendedCodes
  );

  // Reports the full post-change selection to the controlled-mode parent
  // (LES-2). No-op when the prop is absent (CCB). Fires ONLY from the user
  // handlers below (and the dirty-want prune in the recs effect) — never
  // from seeding or reconciliation, which are derivations, not choices —
  // with ONE deliberate exception (AHA-6681 DECISION 1): the
  // derive-and-overwrite path persists its re-derived selection through
  // pendingOverwritePersistRef in the recs effect, bounded to fingerprint
  // mismatches (the saved record must move to the new inventory).
  // Every payload carries the current derivation fingerprint so the
  // persisted record always names the inventory it was made under.
  const emitSelectionsChange = (next: {
    wantIds: number[];
    haveId: number | null;
    refrigerant: string;
    age: string;
  }) => {
    onSelectionsChangeRef.current?.({
      wantIds: next.wantIds,
      haveId: next.haveId,
      refrigerant: next.refrigerant || null,
      age: next.age || null,
      fingerprint: context.derived?.fingerprint,
      // Tile mode always re-carries the tile state (the PATCH replaces the
      // whole record); dropdown-mode payloads keep their exact pre-tile
      // shape.
      ...(variant === 'tiles' ? { virtualCodes, virtualFuel } : {})
    });
  };

  const handleHaveChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    haveDirtyRef.current = true;
    const nextHaveId = e.target.value ? Number(e.target.value) : null;
    setHaveId(nextHaveId);
    emitSelectionsChange({ wantIds, haveId: nextHaveId, refrigerant, age });
  };

  const handleRefrigerantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    refrigerantDirtyRef.current = true;
    setRefrigerant(e.target.value);
    emitSelectionsChange({
      wantIds,
      haveId,
      refrigerant: e.target.value,
      age
    });
  };

  const handleAgeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAge(e.target.value);
    emitSelectionsChange({ wantIds, haveId, refrigerant, age: e.target.value });
  };

  // A checkbox toggle also reports the current selections (LES-3): the
  // controlled parent merges its useRecommendations flag into this payload
  // before persisting, and without the emit a checkbox-only interaction
  // (every select still seeded/derived) would leave it nothing to merge
  // into. Payload shape unchanged — the flag itself is parent-owned. No-op
  // in the CCB (uncontrolled: emitSelectionsChange is a no-op there).
  const handleUseRecommendationsToggle = () => {
    onToggleUseRecommendations();
    emitSelectionsChange({ wantIds, haveId, refrigerant, age });
  };

  const handleWantToggle = (id: number) => {
    wantDirtyRef.current = true;
    // Arm the auto-check intent on a user CHECK; any uncheck (checkbox or
    // chip ✕) disarms it, covering "checked then unchecked before the
    // response landed". A CHECK also lifts the restored-unchecked seed-arm
    // suppression: a fresh gesture supersedes the saved preference.
    const isCheck = !wantIds.includes(id);
    autoCheckIntentRef.current = isCheck;
    if (isCheck) {
      seedAutoCheckSuppressedRef.current = false;
    }
    setWantIds((prev) =>
      prev.includes(id) ? prev.filter((wantId) => wantId !== id) : [...prev, id]
    );
    emitSelectionsChange({
      wantIds: wantIds.includes(id)
        ? wantIds.filter((wantId) => wantId !== id)
        : [...wantIds, id],
      haveId,
      refrigerant,
      age
    });
  };

  // Tiles-variant want gesture: the picker composes a SINGLE want —
  // [resolvedId] when the picks form a catalog type, [] while incomplete —
  // and this replace IS the selection (tiles never holds extras). Dirty (a
  // compose is a user choice — seeding must not clobber it), auto-check
  // armed when a type resolves (a compose IS a check, and it lifts the
  // restored-unchecked suppression like any fresh gesture), disarmed while
  // composing, then the persistence emit.
  const handleWantCompose = (nextWantIds: number[]) => {
    wantDirtyRef.current = true;
    const isCheck = nextWantIds.length > 0;
    autoCheckIntentRef.current = isCheck;
    if (isCheck) {
      seedAutoCheckSuppressedRef.current = false;
    }
    setWantIds(nextWantIds);
    emitSelectionsChange({ wantIds: nextWantIds, haveId, refrigerant, age });
  };

  // Tile-mode user gestures (§A/§D). haveDirty is set immediately so a
  // racing same-fingerprint context reconcile never clobbers the virtual
  // anchor; the legacy saved pick is superseded by the first tile
  // interaction (haveId resolves from the derive, tiles take over).
  const applyVirtualChange = (codes: string[], fuel: string | null) => {
    haveDirtyRef.current = true;
    setLegacySavedPickId(null);
    setVirtualCodes(codes);
    setVirtualFuel(fuel);
    fireDerive(codes, fuel, true);
  };

  // Radio/deselect semantics (incl. virtualFuel lifecycle) live in
  // toggleHaveSelection; fuel arrives baked into the tile identity (v1.1 —
  // the chip row is gone).
  const handleTileToggle = (ref: HaveTileRef) => {
    const next = toggleHaveSelection(virtualCodes, virtualFuel, ref);
    applyVirtualChange(next.codes, next.fuel);
  };

  const handleHaveReset = () => {
    applyVirtualChange([], null);
  };

  // Tiles mode resolves names from the canvas union so a manual pick
  // outside the have-keyed desiredOptions still names its tile/legend
  // entry; wantCanvasOptions IS desiredOptions in dropdown mode.
  const wantName = (id: number) =>
    wantCanvasOptions.find((option) => option.id === id)?.name ?? '';

  const filterText = wantFilter.trim().toLowerCase();
  const visibleOptions = filterText
    ? desiredOptions.filter((option) =>
        option.name.toLowerCase().includes(filterText)
      )
    : desiredOptions;

  // Chips skip ids whose name doesn't resolve (transient reconcile window /
  // cleared options) rather than rendering empty chips.
  const selectedWantChips = wantIds
    .map((id) => ({ id, name: wantName(id) }))
    .filter((chip) => chip.name !== '');

  // Refrigerant/age selects and the LES-3 checkbox are shared verbatim
  // between the dropdown and tile variants (§E: controls UNCHANGED) —
  // extracted so the two returns can't drift.
  const refrigerantControl = (
    <div className='min-w-[180px] flex-1'>
      <label htmlFor='rec-strip-refrigerant' className={SELECT_LABEL_CLASS}>
        Refrigerant
      </label>
      <select
        id='rec-strip-refrigerant'
        className={SELECT_CLASS}
        value={refrigerant}
        onChange={handleRefrigerantChange}
      >
        <option value=''>Not sure</option>
        {context.refrigerantOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );

  const ageControl = (
    <div className='min-w-[180px] flex-1'>
      <label htmlFor='rec-strip-age' className={SELECT_LABEL_CLASS}>
        System age
      </label>
      <select
        id='rec-strip-age'
        className={SELECT_CLASS}
        value={age}
        onChange={handleAgeChange}
      >
        <option value=''>Not sure</option>
        {context.ageOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );

  const useRecommendationsControl = showUseRecommendationsCheckbox && (
    <label
      htmlFor='rec-strip-use-recommendations'
      className={`flex items-center gap-2 text-sm ${
        checkboxDisabled
          ? 'cursor-not-allowed text-gray-400'
          : 'cursor-pointer text-gray-700'
      }`}
      title={
        checkboxDisabled
          ? 'No searchable recommendations for the selected combination'
          : undefined
      }
    >
      <input
        id='rec-strip-use-recommendations'
        type='checkbox'
        checked={useRecommendations}
        disabled={checkboxDisabled}
        onChange={handleUseRecommendationsToggle}
      />
      Only show recommended system types
    </label>
  );

  // Tile-panel variant (AHA-6681, amendment A): TWO visually distinct
  // cards. WHAT YOU HAVE = sectioned tile canvas + readout banner + the
  // refrigerant/age selects (have-attributes, so they live inside the
  // card). WHAT YOU WANT = the always-active component picker (amendment B
  // — compose a system type from per-slot picks; the picker resolves them
  // to the matching catalog type) + the tonnage SYSTEM SIZE row + the flat
  // deduped recommendations. The headed per-want sections do not render
  // here (payload untouched), and the host-owned compatible-systems panel
  // below is untouched.
  if (variant === 'tiles') {
    const equipmentBacked =
      tileInventory != null && tileInventory.codes.length > 0;
    const canResetHave =
      !equipmentBacked &&
      (virtualCodes.length > 0 || haveId != null || legacySavedPickId != null);
    const readout = computeReadout({
      legacySavedPickId,
      equipmentBacked,
      hasTileSelection: tileVirtualCanvas
        ? virtualCodes.length > 0
        : haveId != null || (effectiveCandidateIds?.length ?? 0) > 0,
      haveId,
      candidateIds: effectiveCandidateIds ?? [],
      systemTypes: context.systemTypes
    });
    const showHave = visibleSection === 'have' || visibleSection === 'both';
    const showWant = visibleSection === 'want' || visibleSection === 'both';
    return (
      <div className='mb-4'>
        {showHave && (
          <div
            data-testid='have-card'
            className='mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3'
          >
            <div className='mb-1 flex items-center justify-between gap-3'>
              <div className='text-sm font-semibold text-gray-700'>
                What you have
              </div>
              {canResetHave && (
                <button
                  type='button'
                  className='text-xs font-medium text-blue-600 hover:text-blue-800'
                  onClick={handleHaveReset}
                >
                  Clear
                </button>
              )}
            </div>
            {/* Equipment-backed: no canvas — the locked ReadoutBar below
              already names the full system and points at the equipment
              list. No fingerprint (old API): readout-only degrade (§F) —
              the canvas needs the fingerprint inventory to render
              truthfully. */}
            {tileInventory != null && !equipmentBacked && (
              <HaveTileCanvas
                selectedCodes={virtualCodes}
                fuel={virtualFuel}
                dwelling={dwelling}
                onToggle={handleTileToggle}
              />
            )}
            <ReadoutBar state={readout} />
            <div className='mt-3 flex flex-wrap gap-3'>
              {refrigerantControl}
              {ageControl}
            </div>
          </div>
        )}
        {showWant && (
          <div
            data-testid='want-card'
            className='rounded-lg border border-gray-200 bg-gray-50 p-3'
          >
            <div className='mb-3'>
              {/* Keyed per system so mid-composition picks never leak across
                a system switch whose single wantId happens to match
                (the picker re-syncs from wantIds[0] otherwise). */}
              <WantComponentPicker
                key={systemUniqueId}
                catalog={wantCanvasOptions}
                wantIds={wantIds}
                dwelling={dwelling}
                emptyPlaceholder={desiredSelectPlaceholder(
                  haveId,
                  wantCanvasOptions,
                  effectiveCandidateIds
                )}
                onWantIdsChange={handleWantCompose}
                onPicksChange={onCompositionPicksChange}
                onBlowerFuelChange={onWantFuelChange}
                fallbackFuel={furnaceFuelFromDuctedSelections(
                  wantFuelNarrow ?? []
                )}
              />
              {tonnageSlot}
            </div>
            {recsError && (
              <div className='mb-3 text-xs text-gray-400'>{recsError}</div>
            )}
            {!recsError && matched && (
              <RecommendationsList
                // Same display-only fuel pass as the canvas (AHA-6796):
                // unmapped recs keep their "Not searchable" pill.
                recommendations={filterRecommendationsByFuel(
                  flatRecommendations,
                  wantFuelNarrow ?? []
                )}
                attribution={buildAttributionMap(recommendationsByWant)}
                wantIds={wantIds}
                wantNameOf={wantName}
                mergedAcrossAgeBuckets={mergedAcrossAgeBuckets}
                systemCountsByCode={systemCountsByCode}
                fuelNarrow={wantFuelNarrow ?? []}
              />
            )}
            {!recsError && anchored && wantIds.length > 0 && !matched && (
              <div className='mb-3 text-xs text-gray-400'>
                No recommendations for this combination yet — browse all
                compatible systems below.
              </div>
            )}
            {useRecommendationsControl}
          </div>
        )}
      </div>
    );
  }

  // Id-only key: the server dedupes every rendered list by systemTypeId
  // (AHA-6681), so a duplicate-key warning here means a server regression —
  // don't mask it by re-widening the key.
  const renderRecommendationItems = (recs: ScenarioRecommendation[]) =>
    recs.map((rec) => (
      <li key={rec.systemTypeId} className='mb-0.5'>
        {rec.name}
        {rec.compatibilityCodes.length === 0 && (
          <span className='ml-1.5 rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600'>
            Not searchable
          </span>
        )}
      </li>
    ));

  return (
    <div className='mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3'>
      <div className='mb-1 text-sm font-semibold text-gray-700'>
        Recommended system types
      </div>
      <div className='mb-3 text-xs text-gray-500'>
        Tell us about your current system and what you want to replace it with
        to see which system types we recommend.
      </div>
      <div className='mb-3 flex flex-wrap gap-3'>
        <div className='min-w-[180px] flex-1'>
          <label htmlFor='rec-strip-have' className={SELECT_LABEL_CLASS}>
            What you have
          </label>
          <select
            id='rec-strip-have'
            className={SELECT_CLASS}
            value={haveId ?? ''}
            onChange={handleHaveChange}
          >
            <option value=''>Select your system type</option>
            {systemTypeOptions.map((st) => (
              <option key={st.id} value={st.id}>
                {st.name}
              </option>
            ))}
          </select>
        </div>
        {refrigerantControl}
        {ageControl}
        <div className='min-w-[180px] flex-1'>
          <span id='rec-strip-want-label' className={SELECT_LABEL_CLASS}>
            What you want
          </span>
          {desiredOptions.length > 0 && (
            <input
              type='text'
              aria-label='Filter system types'
              placeholder='Filter...'
              className='mb-1 w-full rounded border border-gray-300 bg-white px-2 py-0.5 text-xs'
              value={wantFilter}
              onChange={(e) => setWantFilter(e.target.value)}
            />
          )}
          <div
            role='group'
            aria-labelledby='rec-strip-want-label'
            className='max-h-32 w-full overflow-y-auto rounded border border-gray-300 bg-white px-2 py-1'
          >
            {desiredOptions.length === 0 && (
              <div className='py-0.5 text-xs text-gray-400'>
                {desiredSelectPlaceholder(
                  haveId,
                  desiredOptions,
                  context.derived?.candidateSystemTypeIds
                )}
              </div>
            )}
            {desiredOptions.length > 0 && visibleOptions.length === 0 && (
              <div className='py-0.5 text-xs text-gray-400'>
                No matching system types
              </div>
            )}
            {visibleOptions.map((st) => (
              <label
                key={st.id}
                className={`flex items-center gap-1.5 py-0.5 text-xs ${
                  !anchored
                    ? 'cursor-not-allowed text-gray-400'
                    : 'cursor-pointer text-gray-700'
                }`}
              >
                <input
                  type='checkbox'
                  checked={wantIds.includes(st.id)}
                  disabled={!anchored}
                  onChange={() => handleWantToggle(st.id)}
                />
                {st.name}
              </label>
            ))}
          </div>
          {/* Selected wants stay dismissible even when the filter hides
              their checkboxes (display-only filtering). */}
          {selectedWantChips.length > 0 && (
            <div className='mt-1 flex flex-wrap gap-1'>
              {selectedWantChips.map((chip) => (
                <span
                  key={chip.id}
                  className='inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-[11px] text-gray-700'
                >
                  {chip.name}
                  <button
                    type='button'
                    aria-label={`Remove ${chip.name}`}
                    className='font-semibold text-gray-500 hover:text-gray-800'
                    onClick={() => handleWantToggle(chip.id)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {recsError && (
        <div className='mb-3 text-xs text-gray-400'>{recsError}</div>
      )}

      {/* Single want: today's flat ranked list, no heading. */}
      {!recsError &&
        matched &&
        recommendationsByWant.length === 1 &&
        recommendationsByWant[0].recommendations.length > 0 && (
          <ol className='mb-3 list-decimal pl-5 text-sm text-gray-900'>
            {renderRecommendationItems(
              recommendationsByWant[0].recommendations
            )}
          </ol>
        )}

      {/* Multiple wants: one headed group per want, in selection order. */}
      {!recsError && matched && recommendationsByWant.length > 1 && (
        <div className='mb-3'>
          {recommendationsByWant.map((group) => (
            <div key={group.desiredSystemTypeId} className='mb-2 last:mb-0'>
              <div className='mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500'>
                {wantName(group.desiredSystemTypeId)}
              </div>
              {group.matched && group.recommendations.length > 0 ? (
                <ol className='list-decimal pl-5 text-sm text-gray-900'>
                  {renderRecommendationItems(group.recommendations)}
                </ol>
              ) : (
                <div className='text-xs text-gray-400'>
                  No recommendations for this combination yet.
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!recsError &&
        matched &&
        flatWantRecommendations.length > 0 &&
        mergedAcrossAgeBuckets && (
          <div className='mb-3 text-xs text-gray-500'>
            Covering both age ranges.
          </div>
        )}

      {!recsError && anchored && wantIds.length > 0 && !matched && (
        <div className='mb-3 text-xs text-gray-400'>
          No recommendations for this combination yet — browse all compatible
          systems below.
        </div>
      )}

      {useRecommendationsControl}
    </div>
  );
}
